/**
 * What stands between a captured microphone and the track the room receives.
 *
 * The shape only: the cleaner itself is a vendor's model and lives in
 * `src/adapters/`, and `useRoom` hands one to `createLocalMedia`. Nothing else
 * processes the outgoing voice; the `clean-the-voice-with-ai-coustics` change
 * says why.
 */

/**
 * What the browser is asked to do to the microphone: cancel the room's own
 * playback, and nothing else. The cleaner removes noise and the listener levels
 * what arrives, so the browser's suppression and gain would be a second and a
 * third opinion; echo is the one thing the cleaner cannot remove.
 */
export const VOICE_CAPTURE: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: false,
  autoGainControl: false,
};

/**
 * What the browser is asked to do to a voice nobody else cleans: everything it
 * can. The raw microphone with suppression and gain off is worse than a plain
 * call, so a room whose cleaner never started or gave up hears this instead.
 */
export const PLAIN_VOICE_CAPTURE: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

export interface CleanOptions {
  /**
   * The cleaner has given up for good on this chain — no token, no model, a
   * processor that failed or stopped keeping up — and the chain is now passing
   * the microphone through as it is.
   */
  onGiveUp?: () => void;
}

export interface CleanedVoice {
  /**
   * The stream to publish. Its track stays the same track whether or not the
   * cleaner is running at this moment, so falling back to the raw microphone
   * is never a track swap.
   */
  readonly stream: MediaStream;
  /**
   * Whether the voice is on air. The vendor bills by the audio its model is
   * handed, so a muted voice is not handed to it, and going live again resumes
   * on the same track. A chain starts live.
   */
  setLive(live: boolean): void;
  dispose(): void;
}

export interface VoiceCleaner {
  /**
   * Puts the cleaner behind `raw`, or answers null when it cannot, in which
   * case the raw microphone is what the room hears.
   */
  clean(
    ctx: AudioContext,
    raw: MediaStream,
    options?: CleanOptions,
  ): Promise<CleanedVoice | null>;
}
